// The match of a room on this screen. The server has the record. This screen
// shows an action of its own at once and sends it, and it plays an action of
// the other screen when the board is quiet. A sync from the server is the
// whole record: the screen keeps what agrees, sends what the server lacks,
// plays what it lacks itself, and takes the record of the server when the two
// do not agree.

import type { RecordedAction } from '../game/replay';
import type { ClientMessage, RoomMatch, RoomView } from '../room/protocol';
import { sideOfSeat } from '../room/protocol';
import { playCoinFlip } from './coinFlip';
import { actionAt, actionCount, gameStore, newMatch } from './game';
import { localSideStore, spectatingStore } from './localSide';
import { matchWaitStore } from './matchWait';
import { queueOpening } from './opening';
import { loadMatchQuietly } from './persist';
import { queueRemote, remoteQueued, resetRemote, setRemoteMisfit } from './remotePlayer';
import { roomStore, updateRoom } from './roomStore';

let sendToRoom: (message: ClientMessage) => void = () => {};
/** The number of the match on the board. Null before the first sync of a match. */
let shownNumber: number | null = null;
/** After a refusal or a misfit, the next sync replaces the match on this screen when the two differ. */
let takeNextSync = false;

/** The parts of an action that say which action it is. */
const sameAction = (a: RecordedAction | undefined, b: RecordedAction): boolean =>
  a !== undefined &&
  JSON.stringify({ ...a, rngState: 0 }) === JSON.stringify({ ...b, rngState: 0 }) &&
  a.rngState === b.rngState;

const askForSync = (): void => {
  takeNextSync = true;
  sendToRoom({ t: 'sync' });
};

/** Call when the screen joins a room. `send` sends a message to the server. */
export const startRoomMatchSync = (send: (message: ClientMessage) => void): void => {
  sendToRoom = send;
  shownNumber = null;
  takeNextSync = false;
  resetRemote();
  setRemoteMisfit(askForSync);
};

/** Call when the screen leaves the room. */
export const endRoomMatch = (): void => {
  sendToRoom = () => {};
  shownNumber = null;
  resetRemote();
};

/** The side of this screen in the match. A spectator sees the board as Sun does. */
const sideIn = (match: RoomMatch): 'gold' | 'violet' => {
  const seat = roomStore.get()?.seat ?? null;
  return seat ? sideOfSeat(match, seat) : 'gold';
};

const loadRecord = (match: RoomMatch): void => {
  resetRemote();
  const loaded = loadMatchQuietly({
    seed: match.seed,
    mode: 'gods',
    casters: match.casters.gold,
    opponentCasters: match.casters.violet,
    actions: match.actions,
  });
  // The record does not replay here: this page has other rules than the server.
  if (!loaded) updateRoom({ status: 'old' });
};

/** A match with no action yet: the coin flip, the opening beats, then the first roll. */
const startFresh = async (match: RoomMatch): Promise<void> => {
  matchWaitStore.set(true);
  resetRemote();
  newMatch(match.seed, 'gods', match.casters.gold, match.casters.violet);
  await playCoinFlip(sideIn(match), spectatingStore.get());
  if (shownNumber !== match.number) return;
  queueOpening(gameStore.get());
  matchWaitStore.set(false);
};

export const receiveSync = (match: RoomMatch | null): void => {
  if (!match) return;
  localSideStore.set(sideIn(match));
  if (match.number !== shownNumber) {
    shownNumber = match.number;
    takeNextSync = false;
    if (match.actions.length === 0) void startFresh(match);
    else {
      loadRecord(match);
      matchWaitStore.set(roomStore.get()?.view?.phase === 'lobby');
    }
    return;
  }
  const take = takeNextSync;
  takeNextSync = false;
  const local = actionCount();
  const server = match.actions.length;
  const shared = Math.min(local, server);
  const agrees = match.actions
    .slice(0, shared)
    .every((action, index) => sameAction(actionAt(index), action));
  if (!agrees || (take && local !== server)) {
    loadRecord(match);
    return;
  }
  if (server > local) {
    // The other screen acted while this one was away. Its actions play as they came.
    resetRemote();
    for (const action of match.actions.slice(local)) queueRemote(action);
  } else if (local > server) {
    // This screen acted while its socket was down. The server gets those actions now.
    for (let index = server; index < local; index += 1) {
      const action = actionAt(index);
      if (action) sendToRoom({ t: 'act', index, action });
    }
  }
};

export const receiveAction = (index: number, action: RecordedAction): void => {
  if (shownNumber === null) return;
  const applied = actionCount();
  if (index < applied) {
    // The echo of an action of this screen, or of one it played already.
    if (!sameAction(actionAt(index), action)) askForSync();
    return;
  }
  const expected = applied + remoteQueued();
  if (index < expected) return;
  if (index === expected) queueRemote(action);
  else askForSync();
};

/** The room went back to the picks, for a rematch or after a seat left. Nobody plays the board. */
export const receiveView = (view: RoomView): void => {
  if (view.phase === 'lobby') matchWaitStore.set(true);
};
